import React from "react";
import PropTypes from "prop-types";
import { Box, Drawer } from "@mui/material";
import Navigation from "../components/common/navigation/Navigation";
import Header from "../components/common/Header/Header";

/**
 * @fileoverview Main Layout Component
 * @module layouts/MainLayout
 * @description The main layout component that provides the overall structure of the application.
 * It includes a persistent sidebar navigation and a main content area.
 *
 * @requires react
 * @requires @mui/material/Box
 * @requires @mui/material/Drawer
 * @requires @mui/material/Toolbar
 * @requires @mui/material/Divider
 * @requires ../components/common/navigation/Navigation
 */

const drawerWidth = 280;
const collapsedDrawerWidth = 76;

/**
 * MainLayout Component
 *
 * Provides the main layout structure for the application with a persistent sidebar navigation.
 * The layout is responsive and adjusts based on the screen size.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - The child components to be rendered in the main content area
 * @returns {JSX.Element} The rendered layout component
 *
 * @example
 * <MainLayout>
 *   <YourComponent />
 * </MainLayout>
 */
const MainLayout = ({ children, isPriorityOutreach, hideSearch }) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "var(--real-vh, 100vh)",
        overflow: "auto",
      }}
    >
      <a
        href="#main-content"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          zIndex: 2000,
          transform: "translateY(-100%)",
          background: "#FFFFFF",
          color: "#000",
          padding: "8px 12px",
          borderRadius: "0 0 4px 0",
        }}
        onFocus={(e) => {
          e.target.style.transform = "translateY(0)";
        }}
        onBlur={(e) => {
          e.target.style.transform = "translateY(-100%)";
        }}
      >
        Skip to main content
      </a>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", md: "none" },
          zIndex: (theme) => theme.zIndex.drawer + 10,
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: drawerWidth,
            backgroundColor: "#403C6F",
            color: "#FFFFFF",
          },
        }}
      >
        <Navigation
          onMobileClose={handleDrawerToggle}
          collapsed={false}
          onToggleCollapse={handleDrawerToggle}
          showCollapseToggle={true}
        />
      </Drawer>

      <Header
        drawerWidth={0}
        handleDrawerToggle={handleDrawerToggle}
        hideSearch={hideSearch}
      />
      <Box
        id="main-content"
        component="section"
        sx={{ flexGrow: 1, overflow: isPriorityOutreach ? "hidden" : "auto" }}
        aria-label="Main content area"
        tabIndex={0}
        onKeyDown={(e) => {
          const el = e.currentTarget;
          const step = 40;
          switch (e.key) {
            case "ArrowDown":
              el.scrollBy({ top: step, behavior: "smooth" });
              e.preventDefault();
              break;
            case "ArrowUp":
              el.scrollBy({ top: -step, behavior: "smooth" });
              e.preventDefault();
              break;
            case "PageDown":
              el.scrollBy({ top: el.clientHeight, behavior: "smooth" });
              e.preventDefault();
              break;
            case "PageUp":
              el.scrollBy({ top: -el.clientHeight, behavior: "smooth" });
              e.preventDefault();
              break;
            case "Home":
              el.scrollTo({ top: 0, behavior: "smooth" });
              e.preventDefault();
              break;
            case "End":
              el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
              e.preventDefault();
              break;
            default:
              break;
          }
        }}
      >
        <Box aria-hidden="true" sx={{ height: { xs: "40px", sm: "42px" } }} />

        {children}
      </Box>
    </Box>
  );
};

export default MainLayout;

MainLayout.propTypes = {
  children: PropTypes.node,
  isPriorityOutreach: PropTypes.bool,
  hideSearch: PropTypes.bool,
};

/**
 * @typedef {Object} MainLayoutProps
 * @property {React.ReactNode} children - Child components to be rendered in the main content area
 */
